import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FaCopy, FaCheck, FaGamepad } from 'react-icons/fa';
import AppStoreBadges from '../components/AppStoreBadges';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg,
    var(--color-primary-dark) 0%,
    var(--color-primary) 50%,
    var(--color-primary-light) 100%
  );
  text-align: center;
`;

const Card = styled.div`
  background: white;
  border-radius: 24px;
  padding: 3rem 2.5rem;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: ${fadeIn} 0.6s ease-out;
`;

const Logo = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: var(--color-primary);
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 2rem;
`;

const InviteCode = styled.div`
  background: #f5f5f5;
  border: 2px dashed var(--color-primary);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`;

const CodeLabel = styled.p`
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Code = styled.p`
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--color-primary);
  letter-spacing: 6px;
  font-family: monospace;
`;

const CopyButton = styled.button<{ copied: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.copied ? '#22c55e' : 'transparent'};
  color: ${props => props.copied ? 'white' : 'var(--color-primary)'};
  border: 1px solid ${props => props.copied ? '#22c55e' : 'var(--color-primary)'};
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.75rem;

  &:hover {
    background: ${props => props.copied ? '#16a34a' : 'var(--color-primary-light)'};
  }
`;

const OpenAppButton = styled.button`
  width: 100%;
  background: var(--color-primary);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  margin-bottom: 1.5rem;
  animation: ${pulse} 2s infinite;

  &:hover {
    background: var(--color-primary-dark);
    transform: translateY(-2px);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  color: #999;
  font-size: 0.85rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #ddd;
  }

  span {
    padding: 0 1rem;
  }
`;

const Footer = styled.p`
  margin-top: 2rem;
  font-size: 0.85rem;
  color: #888;
`;

const JoinRedirectPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [copied, setCopied] = useState(false);
  const [attemptedOpen, setAttemptedOpen] = useState(false);

  const inviteCode = code?.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) || '';
  const appSchemeUrl = `pasurscorekeeper://join/${inviteCode}`;

  // Attempt to open the app on mount
  useEffect(() => {
    if (inviteCode && !attemptedOpen) {
      setAttemptedOpen(true);
      // Try to open the app after a short delay
      const timer = setTimeout(() => {
        window.location.href = appSchemeUrl;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [inviteCode, attemptedOpen, appSchemeUrl]);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleOpenApp = () => {
    window.location.href = appSchemeUrl;
  };

  if (!inviteCode || inviteCode.length !== 6) {
    return (
      <Container>
        <Card>
          <Logo><FaGamepad /></Logo>
          <Title>Invalid Invite Code</Title>
          <Subtitle>
            The invite code in the link appears to be invalid.
            Please ask your friend for a new invite link.
          </Subtitle>
          <AppStoreBadges variant="dark" size="sm" />
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Logo><FaGamepad /></Logo>
        <Title>You're Invited to Play Pasur!</Title>
        <Subtitle>Join the game using the code below</Subtitle>

        <InviteCode>
          <CodeLabel>Invite Code</CodeLabel>
          <Code>{inviteCode}</Code>
          <CopyButton copied={copied} onClick={handleCopyCode}>
            {copied ? <><FaCheck /> Copied!</> : <><FaCopy /> Copy Code</>}
          </CopyButton>
        </InviteCode>

        <OpenAppButton onClick={handleOpenApp}>
          <FaGamepad /> Open in Pasur App
        </OpenAppButton>

        <Divider><span>Don't have the app?</span></Divider>

        <AppStoreBadges variant="dark" size="sm" />

        <Footer>
          After installing, open the app and enter the code above to join the game.
        </Footer>
      </Card>
    </Container>
  );
};

export default JoinRedirectPage;
